<script setup>
import { TreeItem, TreeRoot } from "reka-ui";
import { computed } from "vue";

import { findNode } from "@/utils/tree";

import BaseTooltip from "../BaseTooltip.vue";
import SvgIcon from "../SvgIcon.vue";

const ICON_SIZE = 20;
// SvgIcon forwards width/height to the svg, which declares them as strings
const ICON_PX = `${ICON_SIZE}`;
// Narrower than the expand button, a level only has to read as nested
const INDENT_STEP = 16;
// Left inset of the top level
const BASE_INDENT = 8;

const props = defineProps({
  // Nested nodes: { id, label, icon, children, selectable? }
  items: { type: Array, required: true },
  ariaLabel: { type: String, required: true },
  // The caller owns the selection: a search narrows `items` and drops this whole
  // component while nothing matches, which would lose reka's own selection state
  selectedId: { type: String, default: undefined },
});

const emit = defineEmits(["select"]);

const expandIds = defineModel("expandIds", { type: Array, default: () => [] });

function getKey(item) {
  return item.id;
}

const selectedNode = computed(() => findNode(props.items, props.selectedId));

function hasChildren(item) {
  return !!item.children?.length;
}

function indent(level) {
  return `${BASE_INDENT + (level - 1) * INDENT_STEP}px`;
}

// Reka selects *and* expands on click - only the expand button may expand here.
// Arrow keys toggle through the same event, so only clicks are cancelled.
function cancelToggleFromClick(event) {
  if (event.detail?.originalEvent?.type === "click") event.preventDefault();
}

// `selectable: false` marks structure-only nodes. Cancelling keeps reka from
// selecting them; `disabled` would take expanding away with it.
function onSelect(node, event) {
  if (node.selectable === false) {
    event.preventDefault();
    return;
  }
  emit("select", node);
}
</script>

<template>
  <TreeRoot
    v-slot="{ flattenItems }"
    v-model:expanded="expandIds"
    :model-value="selectedNode"
    class="tree"
    :items
    :get-key
    :get-children="(item) => item.children"
    :aria-label
  >
    <TreeItem
      v-for="item in flattenItems"
      v-slot="{ isExpanded, handleToggle }"
      :key="item._id"
      v-bind="item.bind"
      class="node"
      :class="{ 'not-selectable': item.value.selectable === false }"
      :style="{ paddingInlineStart: indent(item.level) }"
      @select="onSelect(item.value, $event)"
      @toggle="cancelToggleFromClick"
    >
      <button
        v-if="hasChildren(item.value)"
        class="expand-button"
        :aria-label="isExpanded ? 'Collapse' : 'Expand'"
        tabindex="-1"
        @click.stop="handleToggle"
      >
        <SvgIcon
          name="system/chevron-down"
          :width="ICON_PX"
          :height="ICON_PX"
          class="chevron"
          :class="{ collapsed: !isExpanded }"
          aria-hidden="true"
        />
      </button>
      <span v-else class="expand-placeholder" aria-hidden="true" />

      <SvgIcon
        v-if="item.value.icon"
        :name="item.value.icon"
        :width="ICON_PX"
        :height="ICON_PX"
        class="node-icon"
      />
      <BaseTooltip :text="item.value.label" only-show-on-overflow>
        <span class="label">{{ item.value.label }}</span>
      </BaseTooltip>
      <!-- Trailing badges, counts or warnings the caller hangs off a row. The label
           grows to push them right, and the row's gap spaces them: no margins needed -->
      <slot name="suffix" :node="item.value" />
    </TreeItem>
  </TreeRoot>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.tree {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.node {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 8px 0;
  border-radius: 6px;
  color: c.$text;
  cursor: pointer;
  outline: none;

  &:hover {
    background-color: c.$background-alpha-hover;
  }

  &:focus-visible {
    outline: 1px solid c.$border-focus-color;
  }

  &[data-selected] {
    background-color: c.$main;
    color: c.$text-inverse;
  }

  &.not-selectable {
    color: c.$text-subtle;
    cursor: default;

    &:hover {
      background-color: transparent;
    }
  }
}

.expand-button {
  display: flex;
  flex: none;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
}

.expand-placeholder {
  flex: none;
  width: 20px;
}

.node-icon {
  flex: none;
}

.label {
  @include typography.small;

  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: inherit;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chevron {
  transition: transform 0.15s ease;

  &.collapsed {
    transform: rotate(-90deg);
  }
}
</style>
